/* eslint-disable max-lines-per-function */
import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { Box, Select, Stack, Text, Tooltip, useColorModeValue } from '@chakra-ui/react';

import { Components, PanelItem, Size, useSubscription } from '@rta/rta-blueprint-library';

import { ContextPanelContent } from './context-panel-content';
import { InlineNumberInput, StackedStringInput } from './controls';
import { lang } from '@lang';
import { useDesign } from '@hooks';
import { useEffect, useState, useCallback, type JSX } from 'react';

export type SpacerPropertiesPanelProps = {
	id: string | null;
};

export const SpacerPropertiesPanel = ({ id }: SpacerPropertiesPanelProps): JSX.Element => {
	const [controller] = useDesign<BpController>();

	const [panelItem, setPanelItem] = useState<PanelItem | null>(null);
	const [itemSize, setItemSize] = useState<Size | null>(null);

	const [event] = useSubscription(controller?.kitchen ?? null, true, [
		['item_size', 'updated'],
		['component_size', 'updated'],
		['component_placement', 'updated']
	]);
	const readonly = controller?.readonly ?? true;

	useEffect(() => {
		if (panelItem && event) {
			if (event.noun === 'item_size' || event.noun === 'component_size') {
				setItemSize({ ...panelItem.item.size });
			}
		}
	}, [event, panelItem]);

	useEffect(() => {
		if (controller) {
			const refMap = controller.kitchen?.getReferenceMap(id ?? '');
			setPanelItem(refMap.panelItem!);
			setItemSize({ ...refMap.panelItem!.item.size });
		}
	}, [controller, id]);

	const handleWidthChange = useCallback((width: number): void => {
		if (!panelItem) return;
		if (!panelItem.item.override) {
			panelItem.item.override = true;
		}
		panelItem.item.size = { width };
		panelItem.item.cutout = { width };
		setItemSize(prev => prev ? { ...prev, width } : null);
	}, [panelItem]);

	const handleHeightChange = useCallback((height: number): void => {
		if (!panelItem) return;
		if (!panelItem.item.override) {
			panelItem.item.override = true;
		}
		panelItem.item.size = { height };
		panelItem.item.cutout = { height };
		setItemSize(prev => prev ? { ...prev, height } : null);
	}, [panelItem]);

	const handleDepthChange = useCallback((depth: number): void => {
		if (!panelItem) return;
		if (!panelItem.item.override) {
			panelItem.item.override = true;
		}
		panelItem.item.size = { depth };
		panelItem.item.cutout = { depth };
		setItemSize(prev => prev ? { ...prev, depth } : null);
	}, [panelItem]);

	if (!controller || panelItem === null || itemSize === null) return <></>;

	const isFrontOnly = panelItem.item.placement === 'front only';
	const isGenericSpacer = panelItem.item.component?.sku === 'RTA-SPACE-RESERVATION';
	const placementEnabled = !readonly && isGenericSpacer;

	return <ContextPanelContent title={lang.contextPanels.title.spacerProperties}>
		<Stack pt="10px" spacing="0" width="100%">
			<StackedStringInput
				label="Label"
				placeholder="Label..."
				enabled={!readonly}
				value={panelItem.item.component?.customLabel ?? ''}
				onChange={(e) => {
					if (panelItem.item.component) panelItem.item.component.customLabel = e.target.value;
				}}
			/>

			<Box pt="5px" pl="5px">
				<Text color={useColorModeValue('gray.600', 'gray.300')} fontSize="small" fontWeight="medium" mb="1">Placement</Text>
				<Tooltip
					label="Can't be updated on Fixed Reservation SKU's"
					isDisabled={placementEnabled}
					placement="top"
				>
					<Select
						size="sm"
						value={panelItem.item.placement}
						isDisabled={!placementEnabled}
						onChange={(e) => {
							if (panelItem.item.component) {
								if (!panelItem.item.override) {
									panelItem.item.override = true;
								}
								panelItem.item.component.placement = e.target.value;
							}
						}}
					>
						<option value={Components.PLACEMENT_FRONT_ONLY}>Front Only</option>
						<option value={Components.PLACEMENT_TOP_ONLY}>Top Only</option>
					</Select>
				</Tooltip>
			</Box>

			<Text pt="5px" pl="5px" color={useColorModeValue('gray.600', 'gray.300')} fontSize="medium"
				fontWeight="medium"
			>Dimensions</Text>
			<Box marginBottom={3} padding={2} textAlign="left"
				borderLeft="1px solid grey" borderTop="1px" roundedTopLeft="7px"
			>
				<InlineNumberInput
					label="Width" min={0.5}
					value={itemSize.width}
					enabled={!readonly}
					onChange={(valueString) => {
						handleWidthChange(Number(valueString));
					}}
				/>
				{isFrontOnly ? (
					<InlineNumberInput
						label="Overall Height" min={0.5}
						value={itemSize.height}
						enabled={!readonly}
						onChange={(valueString) => {
							handleHeightChange(Number(valueString));
						}}
					/>
				) : (
					<InlineNumberInput
						label="Height" min={0.5}
						value={itemSize.depth}
						enabled={!readonly}
						onChange={(valueString) => {
							handleDepthChange(Number(valueString));
						}}
					/>
				)}
			</Box>
		</Stack>
	</ContextPanelContent>;
};
